<template>
  <div class="measurement-item-wrapper" :class="{ layout, 'overflow-y-auto': totalPages === 1 }">
    <div class="swiper" ref="swiper">
      <div class="swiper-wrapper">
        <div v-for="(_, j) in totalPages" :key="j" class="swiper-slide">
          <div v-for="(m, i) in measurements.slice((j * countPerPage), ((j + 1) * countPerPage))" :key="i"
            class="measurement-item" :class="{ active: activeMeasurement === m }"
            @click="$emit('click', m)">
            <div class="measurement-name">
              <span>
                {{ getMeasurementName(m) }}
              </span>
              <span v-if="isCompare" class="measurement-lr">{{ m.isLeft ? 'L' : (m.isRight ? 'R' : '') }}</span>
            </div>

            <div v-if="isCompare" class="flex flex-row">
              <span v-if="useInch && isLine(m)" class="measurement-value">{{ m.another.value | toInch | toFixed }}</span>
              <span v-else class="measurement-value">{{ m.another.value | toFixed }}</span>
              <img class="mr-2" :src="getComparisonIcon(m)">
              <span v-if="useInch && isLine(m)" class="measurement-value">{{ m.value | toInch | toFixed }}</span>
              <span v-else class="measurement-value">{{ m.value | toFixed }}</span>
            </div>
            <div v-else class="flex flex-row gap-1">
              <span v-if="m.rightMes" class="measurement-lr">L</span>
              <span v-if="useInch && isLine(m)" class="measurement-value">{{ m.value | toInch | toFixed }}</span>
              <span v-else class="measurement-value">{{ m.value | toFixed }}</span>
              <span v-if="m.rightMes" class="measurement-lr">R</span>
              <span v-if="useInch && isLine(m) && m.rightMes" class="measurement-value">{{ m.rightMes.value | toInch | toFixed }}</span>
              <span v-else-if="m.rightMes" class="measurement-value">{{ m.rightMes.value | toFixed }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <step-dots v-if="totalPages > 1" :step="currentPage" :steps="totalPages" class="mx-auto justify-center mb-1"
      style="margin-top: 1rem;" />
  </div>
</template>

<script>
import 'swiper/swiper.min.css';
import Swiper from 'swiper';
import StepDots from '@/components/common/StepDots.vue';
import SizeXtName from '@/modules/sizext-name';

import valueUpSvg from '@/assets/common/value-up.svg';
import valueDownSvg from '@/assets/common/value-down.svg';
import valueEqualSvg from '@/assets/common/value-equal.svg';

const INCH = 0.393700787;

export default {
  name: 'MeasurementPage',
  props: {
    measurements: Array,
    countPerPage: { default: 5, type: Number },
    activeMeasurement: Object,
    useInch: Boolean,
    layout: String,
    isCompare: Boolean,
  },
  components: {
    StepDots,
  },
  data: () => ({
    pageIndex: 0,
  }),
  beforeDestroy() {
    this.destroySwiper();
  },
  mounted() {
    this.initSwiper();
  },
  methods: {
    initSwiper() {
      this.destroySwiper();

      if (this.totalPages === 1) return;

      this.$swiper = new Swiper(this.$refs.swiper);
      this.$swiper.on('activeIndexChange', () => {
        this.pageIndex = this.$swiper.activeIndex;
      });
    },
    destroySwiper() {
      if (!this.$swiper) return;
      this.$swiper.destroy();
      this.$swiper = null;
      this.pageIndex = 0;
    },
    getComparisonIcon(m) {
      if (Math.abs(m.value - m.another.value) < 0.01) return valueEqualSvg;
      if (m.value < m.another.value) return valueDownSvg;
      if (m.value > m.another.value) return valueUpSvg;
      return valueEqualSvg;
    },
    getMeasurementName(m) {
      const sizextName = SizeXtName.getName(m.key, m.subKey);
      const name = m.nameKey ? this.$i18n.t(`measurement.${m.nameKey}`) : sizextName;
      if (m.type === 'angle')
        return `${name} (°)`;
      if (m.type === 'volume')
        return `${name} (cc)`;
      return name;
    },
    isLine(m) {
      return m.type === 'line';
    },
  },
  computed: {
    currentPage() {
      return this.pageIndex + 1;
    },
    totalPages() {
      return Math.ceil(this.measurements.length / this.countPerPage);
    },
  },
  filters: {
    toFixed(value) {
      if (!Number.isFinite(value)) return '--';
      const val = parseFloat(value);
      if (val > 100) return val.toFixed(1);
      return val.toFixed(2);
    },
    toInch(value) {
      return value * INCH;
    },
  },
  watch: {
    totalPages() {
      this.$nextTick(() => {
        this.$swiper.update();
        this.$swiper.slideTo(0);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.measurement-item-wrapper {
  @apply flex flex-1 flex-col w-full;
}

.measurement-item {
  @apply w-full flex items-center p-4 justify-between;
  height: 44px;
  color: #8c8c8c;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  border: 1px solid #BFBFBF;
  border-radius: 8px;

  &.active {
    color: #fff;
    background: linear-gradient(270deg, #2245FF 0.85%, #4300D9 100%);

    .measurement-lr {
      color: #fff;
    }
  }

  .measurement-lr {
    color: #4658FF;
  }

  .measurement-name {
    width: 55%;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .measurement-value {
    width: 48px;
  }
}

.swiper {
  @apply w-full h-full;
}

.swiper-slide {
  @apply flex flex-col w-full h-auto px-6;
  gap: 12px;
}

.swiper-pagination {
  @apply text-center;
}
</style>
